import React, { Component } from "react";
import { templateStyles } from "container-components/templates/styles/version-16";
import propTypes from "prop-types";
import injectSheet from "react-jss";
import landerDefaults from "server-util/lander_versions/version-16";
import { hexToRgb } from "util/colours";
import BackgroundOverlay from "v2/components/templates/BackgroundOverlay";
import Disclaimer from "v2/components/templates/Disclaimer";
import FormContainer from "v2/components/templates/FormContainer";
import HeroImage from "v2/components/templates/HeroImage";
import Logo from "v2/components/templates/Logo";
import PartnerLogos from "v2/components/templates/PartnerLogos";
import TemplateContainer from "v2/components/templates/TemplateContainer";
import Text from "v2/components/templates/Text";
import styleHelper from "v2/styles/style-helper";
import NotStartedWarning from "./NotStartedWarning";

const styles = {
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(30px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  "@keyframes floatBlob": {
    "0%": {
      transform: "translate(-50%, 0) scale(1)",
      borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    },
    "25%": {
      transform: "translate(-50%, -20px) scale(1.05)",
      borderRadius: "30% 60% 70% 40% / 50% 60% 30% 60%",
    },
    "50%": {
      transform: "translate(-50%, 0) scale(1)",
      borderRadius: "50% 60% 30% 60% / 30% 60% 70% 40%",
    },
    "75%": {
      transform: "translate(-50%, 20px) scale(0.95)",
      borderRadius: "60% 40% 60% 40% / 70% 30% 40% 60%",
    },
    "100%": {
      transform: "translate(-50%, 0) scale(1)",
      borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    },
  },
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.5,
    },
  },
  "@keyframes checkMarkSlide": {
    "0%": {
      opacity: 0,
      transform: "translateX(-10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },
  "@keyframes arrowBounce": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-8px)",
    },
  },
  "@keyframes buttonGlow": {
    "0%, 100%": {
      boxShadow: "0 10px 30px rgba(139, 92, 246, 0.4), 0 0 20px rgba(139, 92, 246, 0.2)",
    },
    "50%": {
      boxShadow: "0 10px 40px rgba(139, 92, 246, 0.6), 0 0 30px rgba(139, 92, 246, 0.4)",
    },
  },
  heroSection: {
    position: "relative",
    width: "100%",
    minHeight: "60vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    padding: "100px 40px",
    [styleHelper.max(768)]: {
      padding: "80px 20px",
      minHeight: "50vh",
    },
    [styleHelper.max(480)]: {
      padding: "40px 12px",
      minHeight: "auto",
    },
  },
  floatingBlob1: {
    position: "absolute",
    width: "500px",
    height: "500px",
    top: "10%",
    left: "50%",
    background: "radial-gradient(circle, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0))",
    borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    filter: "blur(40px)",
    animation: "$floatBlob 15s ease-in-out infinite",
    zIndex: 1,
    [styleHelper.max(768)]: {
      width: "300px",
      height: "300px",
    },
  },
  floatingBlob2: {
    position: "absolute",
    width: "400px",
    height: "400px",
    bottom: "20%",
    left: "50%",
    background: "radial-gradient(circle, rgba(6, 182, 212, 0.2), rgba(6, 182, 212, 0))",
    borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    filter: "blur(40px)",
    animation: "$floatBlob 18s ease-in-out infinite reverse",
    zIndex: 1,
    [styleHelper.max(768)]: {
      width: "250px",
      height: "250px",
    },
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    maxWidth: "1200px",
    width: "100%",
    textAlign: "center",
    animation: "$fadeInUp 1s ease-out",
    [styleHelper.max(768)]: {
      padding: "0 20px",
    },
    [styleHelper.max(480)]: {
      padding: "0 8px",
    },
  },
  headlineContainer: {
    marginBottom: "40px",
    "& p": {
      margin: "0 !important",
    },
    "& span": {
      textShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
    },
  },
  preEntryMessageContainer: {
    marginBottom: "60px",
    maxWidth: "800px",
    margin: "0 auto 60px",
    "& p": {
      margin: "0 !important",
    },
    "& span": {
      textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
    },
  },
  scrollButton: {
    background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    color: "#ffffff",
    border: "none",
    borderRadius: "60px",
    padding: "20px 50px",
    fontSize: "16px",
    fontWeight: "700",
    fontFamily: "'Plus Jakarta Sans'",
    cursor: "pointer",
    boxShadow: "0 10px 30px rgba(139, 92, 246, 0.4), 0 0 20px rgba(139, 92, 246, 0.2)",
    transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    margin: "0 auto",
    position: "relative",
    overflow: "hidden",
    letterSpacing: "1px",
    animation: "$buttonGlow 3s ease-in-out infinite",
    "&:before": {
      content: '""',
      position: "absolute",
      top: "-50%",
      left: "-50%",
      width: "200%",
      height: "200%",
      background: "linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
      transform: "rotate(45deg)",
      transition: "all 0.6s",
    },
    "&:hover": {
      transform: "translateY(-4px) scale(1.05)",
      boxShadow: "0 20px 50px rgba(139, 92, 246, 0.6), 0 0 40px rgba(139, 92, 246, 0.4)",
      "&:before": {
        left: "100%",
      },
    },
    "&:active": {
      transform: "translateY(-1px) scale(1.02)",
      boxShadow: "0 8px 20px rgba(139, 92, 246, 0.5)",
    },
    [styleHelper.max(768)]: {
      padding: "18px 40px",
      fontSize: "15px",
      gap: "14px",
    },
    [styleHelper.max(480)]: {
      padding: "12px 28px",
      fontSize: "13px",
      gap: "8px",
      letterSpacing: "0.5px",
    },
    [styleHelper.max(380)]: {
      padding: "10px 24px",
      fontSize: "12px",
      gap: "6px",
    },
  },
  scrollArrow: {
    width: "0",
    height: "0",
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderTop: "12px solid white",
    animation: "$arrowBounce 2s ease-in-out infinite",
    [styleHelper.max(480)]: {
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: "9px solid white",
    },
  },
  gradientWrapper: {
    position: "relative",
    background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #fdf4ff 100%)",
  },
  formSection: {
    position: "relative",
    padding: "100px 40px 0 40px",
    [styleHelper.max(768)]: {
      padding: "60px 20px 0 20px",
    },
  },
  formContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexDirection: (props) => (props.desktopVersion ? "row" : "column"),
    gap: "60px",
    alignItems: "flex-start",
    position: "relative",
    zIndex: 2,
    [styleHelper.max(1100)]: {
      flexDirection: "column !important",
      gap: "40px !important",
    },
  },
  leftColumn: {
    flex: "1 1 45%",
    paddingTop: "30px",
    [styleHelper.max(1100)]: {
      flex: "1 1 100% !important",
      width: "100%",
    },
  },
  rightColumn: {
    flex: "1 1 55%",
    [styleHelper.max(1100)]: {
      flex: "1 1 100% !important",
      width: "100%",
    },
  },
  detailsHeadingContainer: {
    marginBottom: "40px",
    paddingTop: "8px",
    textAlign: "center",
    "& p": {
      margin: "0 !important",
    },
    "& span": {
      fontWeight: "700 !important",
      background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
    [styleHelper.max(768)]: {
      marginBottom: "30px",
    },
  },
  detailsContainer: {
    "& ul": {
      listStyle: "none",
      padding: 0,
      margin: 0,
    },
    "& li": {
      position: "relative",
      paddingLeft: "50px",
      marginBottom: "24px",
      fontSize: "16px",
      lineHeight: "1.6",
      transition: "transform 0.2s ease",
      animation: "$checkMarkSlide 0.5s ease-out",
      "&:hover": {
        transform: "translateX(5px)",
      },
      "&:before": {
        content: '"✓"',
        position: "absolute",
        left: 0,
        color: "#ffffff",
        fontWeight: "bold",
        fontSize: "20px",
        width: "36px",
        height: "36px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
      },
    },
  },
  formBox: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "48px",
    boxShadow: "0 10px 40px rgba(139, 92, 246, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(139, 92, 246, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 15px 50px rgba(139, 92, 246, 0.2), 0 6px 20px rgba(0, 0, 0, 0.12)",
    },
    [styleHelper.max(768)]: {
      padding: "32px",
    },
    [styleHelper.max(480)]: {
      padding: "24px",
    },
  },
  bottomBlob: {
    position: "absolute",
    bottom: "-10%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "120%",
    height: "40%",
    background: "linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(6, 182, 212, 0.15))",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    animation: "$floatBlob 20s ease-in-out infinite",
    zIndex: 1,
    filter: "blur(40px)",
    [styleHelper.max(768)]: {
      width: "150%",
      height: "30%",
    },
  },
  prizingLogoContainer: {
    marginTop: "48px",
    textAlign: "center",
    [styleHelper.max(768)]: {
      marginTop: "32px",
    },
  },
  prizingLogoText: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#64748b",
    marginBottom: "20px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    fontFamily: "'Plus Jakarta Sans'",
  },
  prizingLogoImg: {
    maxWidth: "100%",
    height: "auto",
  },
  bottomSection: {
    background: "transparent",
    padding: "80px 40px 60px 40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "40px",
    [styleHelper.max(768)]: {
      padding: "60px 20px 40px 20px",
      gap: "30px",
    },
  },
  disclaimerContainer: {
    maxWidth: "1200px",
    width: "100%",
    textAlign: "center",
  },
};

class Version16 extends Component {
  static propTypes = {
    opts: propTypes.object.isRequired,
    classes: propTypes.object.isRequired,
    desktopVersion: propTypes.bool,
    isEditorMenuOpen: propTypes.bool,
    updateAttr: propTypes.func,
    updateElementsAttr: propTypes.func,
  };

  static defaultProps = {
    desktopVersion: true,
    isEditorMenuOpen: false,
  };

  static childContextTypes = {
    baseStyles: propTypes.object,
    userStyles: propTypes.object,
  };

  getChildContext() {
    const { classes: css, opts: { elements: { styles: userStyles = {} } = {} } = {} } = this.props;

    return {
      baseStyles: css,
      userStyles: userStyles || {},
    };
  }

  render() {
    const {
      opts: { campaign = {}, hostBrand = {}, elements = {}, termsLink, acceptedInvites = [] } = {},
      updateElementsAttr,
      editElements,
      customFonts = [],
      edit,
      updateAttr,
      agreeToLiability,
      classes: css,
      desktopVersion,
      isEditorMenuOpen,
    } = this.props;

    // Use defaults from lander version config, then override with campaign elements
    const safeElements = {
      ...landerDefaults,
      ...elements,
    };

    // Ensure styles.fonts exists with required properties for form components
    safeElements.styles = { ...landerDefaults.styles, ...safeElements.styles };
    safeElements.styles.fonts = {
      ...landerDefaults.styles.fonts,
      ...safeElements.styles.fonts,
    };

    const isBackgroundVideo =
      safeElements.mainImage && safeElements.mainImage.split(".").pop() === "mp4";

    // Scroll button computed styles
    const scrollButtonColor = safeElements.styles?.fonts?.scrollButtonColor?.color?.color;
    const scrollButtonTextColor = safeElements.styles?.fonts?.scrollButtonTextColor?.color?.color;
    const scrollButtonRgb = hexToRgb(scrollButtonColor);
    const scrollButtonShadow = scrollButtonRgb
      ? `0 10px 30px rgba(${scrollButtonRgb}, 0.4), 0 0 20px rgba(${scrollButtonRgb}, 0.2)`
      : undefined;

    return (
      <TemplateContainer
        campaign={campaign}
        elements={safeElements}
        isFixedBackgroundImage={false}
        desktopVersion={desktopVersion}
        updateAttr={updateElementsAttr}
        campaignUpdateAttr={updateAttr}
        acceptedInvites={acceptedInvites}
        isEditorMenuOpen={isEditorMenuOpen}
        hostBrand={hostBrand}
      >
        {/* Hero Section */}
        <div className={css.heroSection}>
          {safeElements.mainImage && (
            <HeroImage
              imageUrl={safeElements.mainImage}
              desktopVersion={desktopVersion}
              isVideo={isBackgroundVideo}
            />
          )}
          <BackgroundOverlay desktopVersion={desktopVersion} />

          <div className={css.floatingBlob1}></div>
          <div className={css.floatingBlob2}></div>

          <div className={css.heroContent}>
            {safeElements.headLine && (
              <Text
                model={safeElements}
                updateAttr={updateElementsAttr}
                edit={editElements}
                attr="headLine"
                customFonts={customFonts}
                className={css.headlineContainer}
              />
            )}

            {safeElements.preEntryMessage && (
              <Text
                model={safeElements}
                updateAttr={updateElementsAttr}
                edit={editElements}
                attr="preEntryMessage"
                customFonts={customFonts}
                className={css.preEntryMessageContainer}
              />
            )}

            {(safeElements.styles?.showScrollButton ?? safeElements.showScrollButton ?? true) && (
              <button
                className={`${css.scrollButton} scroll-to-form`}
                style={{
                  background: scrollButtonColor
                    ? `linear-gradient(135deg, ${scrollButtonColor} 0%, ${scrollButtonColor}dd 100%)`
                    : undefined,
                  color: scrollButtonTextColor ?? "#ffffff",
                  boxShadow: scrollButtonShadow,
                  animation: scrollButtonRgb ? "none" : undefined,
                }}
              >
                <span>
                  {safeElements.styles?.scrollButtonText ??
                    safeElements.scrollButtonText ??
                    "ENTER TO WIN"}
                </span>
                <div
                  className={css.scrollArrow}
                  style={{
                    borderTopColor: scrollButtonTextColor ?? "#ffffff",
                  }}
                ></div>
              </button>
            )}
          </div>
        </div>

        {/* Gradient Wrapper - Continuous Background */}
        <div className={css.gradientWrapper}>
          {/* Form Section */}
          <div className={css.formSection}>
            <div className={css.formContainer}>
              <div className={css.leftColumn}>
                {safeElements.detailsHeading && (
                  <Text
                    model={safeElements}
                    updateAttr={updateElementsAttr}
                    edit={editElements}
                    attr="detailsHeading"
                    customFonts={customFonts}
                    className={css.detailsHeadingContainer}
                  />
                )}
                {safeElements.details && (
                  <Text
                    model={safeElements}
                    updateAttr={updateElementsAttr}
                    edit={editElements}
                    attr="details"
                    customFonts={customFonts}
                    className={css.detailsContainer}
                  />
                )}

                {(safeElements.prizing_logo || campaign.prizingLogo) && (
                  <div className={css.prizingLogoContainer}>
                    <div className={css.prizingLogoText}>
                      {safeElements.prizingPartnerText || "PRIZING PROVIDED BY"}
                    </div>
                    <Logo
                      logoPath={safeElements.prizing_logo || campaign.prizingLogo}
                      updateAttr={updateElementsAttr}
                      edit={editElements}
                      campaign={campaign}
                      elements={safeElements}
                      maxHeight={
                        safeElements.styles.prizingLogo
                          ? safeElements.styles.prizingLogo.height
                          : 100
                      }
                      className={css.prizingLogoImg}
                      id="prizing-logo"
                      allowDelete
                      editName="prizing-logo"
                      uploadButtonLabel="Upload a prizing logo"
                    />
                  </div>
                )}
              </div>

              <div className={css.rightColumn}>
                <div className={css.formBox}>
                  <FormContainer
                    updateAttr={updateAttr}
                    updateElementsAttr={updateElementsAttr}
                    campaign={campaign}
                    elements={safeElements}
                    edit={edit}
                    editElements={editElements}
                    termsLink={termsLink}
                    agreeToLiability={agreeToLiability}
                    acceptedInvites={acceptedInvites}
                    customFonts={customFonts}
                    desktopVersion={desktopVersion}
                    preEntryStyles={{}}
                    hidePartnerInformation={true}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className={css.bottomSection}>
            {acceptedInvites && acceptedInvites.length > 0 && (
              <PartnerLogos
                updateAttr={updateElementsAttr}
                edit={editElements}
                campaign={safeElements}
                partnerCampaigns={acceptedInvites}
                desktopVersion={desktopVersion}
              />
            )}

            {campaign.disclaimer && (
              <div className={css.disclaimerContainer}>
                <Disclaimer
                  campaign={campaign}
                  updateAttr={updateAttr}
                  edit={edit}
                  customFonts={customFonts}
                  onlyTextComponent={false}
                />
              </div>
            )}
          </div>
        </div>

        <NotStartedWarning campaign={campaign} />
      </TemplateContainer>
    );
  }
}

const StyledVersion16 = injectSheet(styles)(Version16);
export default injectSheet(templateStyles)(StyledVersion16);
